import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { openInAppTab } from "../../utils/tabNavigation";
import SearchMapAddCompany from "../../components/Modals/workstation/SearchMapAddCompany";
import WhoWeKnow from "../../components/Modals/workstation/WhoWeKnow";
import { handleDeleteWithConfirm } from "../../utils/sweetalert";
import toastr from "../../utils/toast";

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// The column the map puts companies with no PINE industry in (searchMap.js).
const UNCLASSIFIED = "Unclassified";

// The prototype's colours.
const HEADER_ORANGE = "#f26b2a";
const GREEN = "hsl(142 71% 45%";
const RED = "hsl(0 84% 60%";

const CELL_STYLE = {
  mapped: { background: `${GREEN} / 0.25)` },
  no_poach: { background: `${RED} / 0.8)`, color: "#fff" },
  not_mapped: { background: "#fff" },
};

const KEY = [
  { status: "mapped", label: "Mapped", swatch: `${GREEN} / 0.6)` },
  { status: "no_poach", label: "No-poach", swatch: `${RED} / 0.8)` },
  { status: "not_mapped", label: "Not yet mapped", swatch: "#fff" },
];

const CELL_TITLE = {
  mapped: "Mapped - candidates from here are on this role",
  no_poach: "No-poach - a client we cannot source from",
  not_mapped: "Not yet mapped - nobody from here is on this role yet",
};

const HOW_IT_WORKS = (
  <Popover style={{ maxWidth: 320 }}>
    <Popover.Body className="small">
      <p className="fw-semibold mb-1">Adding companies</p>
      <p className="text-muted">
        Use <strong>Add company</strong> at the top, or the{" "}
        <strong>+ Add</strong> row at the bottom of any industry column to add
        straight into that column. You can also create a brand new industry
        there.
      </p>
      <p className="fw-semibold mb-1">What the colours mean</p>
      <p className="text-muted mb-0">
        New companies start white (not yet mapped). A company turns green
        automatically once candidates are mapped against it in the workstation,
        and red means no-poach. Click a candidate count to see the executives
        mapped there.
      </p>
    </Popover.Body>
  </Popover>
);

const CANDIDATE_COLUMNS = [
  "ID",
  "Name",
  "Email",
  "Phone",
  "Designation",
  "Current Company",
  "Status",
];

const userName = (u) =>
  [u.firstname, u.lastname]
    .map((s) => String(s || "").trim())
    .filter(Boolean)
    .join(" ");

/**
 * Search Map - one map per role: industries (columns) x target companies.
 * A company's colour comes from the backend on every load: green once a
 * candidate from it is on this role, red for no-poach, white otherwise.
 */
export default function SearchMap() {
  const { id } = useParams();
  const { token } = useOutletContext();
  // Always the role's workstation page. A history-based Back did nothing in a
  // tab PINE did not open itself but that still carried its "opened by PINE"
  // mark - it tried to close the tab, and the browser refused.
  const navigate = useNavigate();
  const handleBack = () => navigate(`/workstation/view/${id}`);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadMap = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${REACT_APP_API_BASE_URL}/workstation/search_map/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMap(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load the search map.");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    loadMap();
  }, [loadMap]);

  // Active PINE users for the owner dropdowns. If it fails the owner row
  // still shows the current owner; it just cannot be changed.
  const [users, setUsers] = useState([]);
  useEffect(() => {
    axios
      .get(`${REACT_APP_API_BASE_URL}/common_controller/get_active_users`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUsers([]));
  }, [token]);

  // Companies PINE has no industry for sit in "Unclassified". Opening the map
  // starts classifying them in the background - a LinkedIn page per company,
  // then PINE's industry - and the map reloads with them in their columns.
  const classifyUrl = `${REACT_APP_API_BASE_URL}/workstation/gap_analysis/${id}/classify`;
  const [classify, setClassify] = useState(null);
  const [autoStarted, setAutoStarted] = useState(false);
  const unclassified = (map?.columns || []).find(
    (c) => c.industry === UNCLASSIFIED,
  );

  const startClassify = useCallback(
    async (force = false) => {
      try {
        const res = await axios.post(
          classifyUrl,
          { force },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setClassify((prev) => ({ ...prev, ...res.data }));
        if (!res.data.run) {
          const state = await axios.get(classifyUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setClassify(state.data);
        }
      } catch (err) {
        if (force) {
          toastr.error(err.response?.data?.error || "Could not start classifying.");
        }
      }
    },
    [classifyUrl, token],
  );

  useEffect(() => {
    if (map && !autoStarted && unclassified?.companies.length) {
      setAutoStarted(true);
      startClassify(false);
    }
  }, [map, autoStarted, unclassified, startClassify]);

  // While a run is going, check every few seconds; when it ends, reload the map.
  const classifyRunning = classify?.run?.status === "running";
  useEffect(() => {
    if (!classifyRunning) return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await axios.get(classifyUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClassify(res.data);
        if (res.data.run?.status !== "running") loadMap();
      } catch {
        // A missed check is retried on the next tick.
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [classifyRunning, classifyUrl, token, loadMap]);

  // Under the Unclassified column's heading.
  const classifyNote = () => {
    if (classifyRunning) {
      return (
        <div className="fw-normal d-flex align-items-center gap-1 mt-1">
          <span className="spinner-border spinner-border-sm" role="status"></span>
          Classifying{classify.pending ? ` ${classify.pending}` : ""}{" "}
          {classify.pending === 1 ? "company" : "companies"}…
        </div>
      );
    }
    const left = classify?.not_placed || 0;
    const failed = classify?.run?.status === "failed";
    if (!left && !failed) return null;
    return (
      <div className="fw-normal mt-1">
        {failed
          ? "Classifying did not finish. "
          : `${left} could not be placed. `}
        <button
          type="button"
          className="btn btn-link p-0 text-white text-decoration-underline"
          style={{ fontSize: 11 }}
          onClick={() => startClassify(true)}
        >
          Try again
        </button>
      </div>
    );
  };

  // The Add company dialog: null when closed, else the column it was opened
  // from (null from the header).
  const [adding, setAdding] = useState(null);
  const openAdd = (column = null) => setAdding({ column });

  const base = `${REACT_APP_API_BASE_URL}/workstation/search_map/${id}`;

  const setOwner = async (column, value) => {
    try {
      await axios.put(
        `${base}/column/${column.id}/owner`,
        { owner_user_id: value ? Number(value) : null },
        auth,
      );
      await loadMap();
    } catch (err) {
      toastr.error(err.response?.data?.error || "Could not set the owner.");
    }
  };

  const removeCompany = (company) =>
    handleDeleteWithConfirm({
      title: `Remove ${company.name}?`,
      text: "It comes off this map only. PINE's own company record is not touched.",
      confirmButtonText: "Remove",
      confirmCallback: async () => {
        try {
          await axios.delete(`${base}/company/${company.id}`, auth);
          toastr.success(`${company.name} removed from the map.`);
          await loadMap();
        } catch (err) {
          toastr.error(
            err.response?.data?.error || "Could not remove the company.",
          );
        }
      },
    });

  const removeColumn = (column) =>
    handleDeleteWithConfirm({
      title: `Remove ${column.industry}?`,
      text: `The industry and its ${column.companies.length} ${
        column.companies.length === 1 ? "company" : "companies"
      } come off this map. PINE's own company records are not touched.`,
      confirmButtonText: "Remove",
      confirmCallback: async () => {
        try {
          await axios.delete(`${base}/column/${column.id}`, auth);
          toastr.success(`${column.industry} removed from the map.`);
          await loadMap();
        } catch (err) {
          toastr.error(
            err.response?.data?.error || "Could not remove the industry.",
          );
        }
      },
    });

  // The candidates behind a badge: the company, then its list once loaded.
  const [viewing, setViewing] = useState(null);
  const [people, setPeople] = useState(null);
  const [peopleError, setPeopleError] = useState("");

  const openCandidates = async (company) => {
    setViewing(company);
    setPeople(null);
    setPeopleError("");
    try {
      const res = await axios.get(
        `${base}/company/${company.id}/candidates`,
        auth,
      );
      setPeople(res.data?.candidates || []);
    } catch (err) {
      setPeopleError(
        err.response?.data?.error || "Could not load the candidates.",
      );
    }
  };

  // "Who do we already know there?" for one company on the map.
  const [whoWeKnow, setWhoWeKnow] = useState(null);

  const columns = map?.columns || [];
  const companies = columns.flatMap((c) => c.companies);
  const candidates = companies.reduce((sum, c) => sum + c.candidates, 0);
  const countOf = (status) =>
    companies.filter((c) => c.status === status).length;
  const rows = Math.max(0, ...columns.map((c) => c.companies.length));

  return (
    <div className="content">
      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
            <div>
              <h4 className="mb-1">
                Search Map{map ? ` — ${map.role.name}` : ""}
              </h4>
              {map && (
                <p className="text-muted mb-0">
                  {columns.length} industries · {companies.length} companies ·{" "}
                  {candidates} candidates mapped · a company turns green only
                  once candidates are mapped against it
                </p>
              )}
            </div>
            <div className="d-flex align-items-center justify-content-end flex-wrap gap-3 small ms-auto">
              {map &&
                KEY.map((k) => (
                  <span
                    key={k.status}
                    className="d-inline-flex align-items-center gap-1"
                  >
                    <span
                      className="d-inline-block rounded-1 border"
                      style={{ width: 12, height: 12, background: k.swatch }}
                    />
                    {k.label} ({countOf(k.status)})
                  </span>
                ))}
              <OverlayTrigger
                trigger="click"
                placement="bottom-end"
                rootClose
                overlay={HOW_IT_WORKS}
              >
                <button className="btn btn-link btn-sm text-decoration-none p-0">
                  <i className="fa-regular fa-circle-question me-1"></i> How it
                  works
                </button>
              </OverlayTrigger>
              <button
                className="btn btn-primary btn-sm d-inline-flex align-items-center"
                onClick={() => openAdd()}
                disabled={!map}
              >
                <i className="fa-solid fa-circle-plus me-1"></i> Add company
              </button>
              <Link
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
                to={`/workstation/gap_analysis/${id}`}
                state={{ returnTo: `/workstation/search_map/${id}` }}
              >
                <i className="fa-solid fa-wand-magic-sparkles me-1"></i> Gap
                Analysis
              </Link>
              <button
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
                onClick={handleBack}
              >
                <i className="ti ti-arrow-left me-1"></i> Back to workstation
              </button>
            </div>
          </div>

          {/* Only the first load shows this; a reload keeps the old map up. */}
          {loading && !map && (
            <p className="text-muted mb-0">Loading the search map…</p>
          )}

          {!loading && error && (
            <div className="alert alert-danger mb-0">{error}</div>
          )}

          {map && !error && columns.length === 0 && (
            <div className="text-center border rounded py-5 px-3">
              <i className="fa-solid fa-sitemap fs-1 text-muted mb-3 d-block"></i>
              <h5 className="mb-1">This map is empty</h5>
              <p className="text-muted mb-3">
                Add the companies you plan to source from, grouped by industry.
                A company turns green once candidates from it are on this role.
              </p>
              <button
                className="btn btn-primary btn-sm d-inline-flex align-items-center"
                onClick={() => openAdd()}
              >
                <i className="fa-solid fa-circle-plus me-1"></i> Add company
              </button>
            </div>
          )}

          {map && !error && columns.length > 0 && (
            <>
              <div className="border rounded overflow-auto">
                <table
                  className="mb-0"
                  style={{ borderCollapse: "collapse", fontSize: 12 }}
                >
                  <thead>
                    <tr>
                      {columns.map((c) => {
                        const mapped = c.companies.filter(
                          (x) => x.status === "mapped",
                        ).length;
                        const peopleIn = c.companies.reduce(
                          (sum, x) => sum + x.candidates,
                          0,
                        );
                        return (
                          <th
                            key={c.id}
                            className="text-start fw-semibold px-3 py-2 border align-top"
                            style={{
                              background: HEADER_ORANGE,
                              color: "#fff",
                              minWidth: 170,
                            }}
                          >
                            <div className="d-flex align-items-start justify-content-between gap-2">
                              <div>
                                <div>{c.industry}</div>
                                <div
                                  className="fw-normal"
                                  style={{ opacity: 0.85 }}
                                >
                                  {mapped}/{c.companies.length} mapped ·{" "}
                                  {peopleIn} candidates
                                </div>
                                {c.industry === UNCLASSIFIED && classifyNote()}
                              </div>
                              <button
                                type="button"
                                onClick={() => removeColumn(c)}
                                title={`Remove ${c.industry} from the map`}
                                className="btn btn-link p-0 text-white"
                                style={{ fontSize: 11, opacity: 0.8 }}
                              >
                                <i className="fa-solid fa-xmark"></i>
                              </button>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: rows }).map((_, ri) => (
                      <tr key={ri}>
                        {columns.map((c) => {
                          const x = c.companies[ri];
                          return (
                            <td
                              key={c.id}
                              className="border px-3 py-1 text-nowrap"
                              style={x ? CELL_STYLE[x.status] : undefined}
                              title={
                                x
                                  ? x.linked
                                    ? CELL_TITLE[x.status]
                                    : "Added by name only - not linked to a PINE company, so it stays white"
                                  : undefined
                              }
                            >
                              {x && (
                                <span className="d-flex align-items-center justify-content-between gap-2">
                                  <span className="d-flex align-items-center gap-1">
                                    {x.name}
                                    {x.link && (
                                      <a
                                        href={x.link}
                                        target="_blank"
                                        rel="noreferrer"
                                        title={`LinkedIn / domain: ${x.link}`}
                                        style={{ color: "inherit", opacity: 0.6 }}
                                      >
                                        <i
                                          className="fa-solid fa-arrow-up-right-from-square"
                                          style={{ fontSize: 10 }}
                                        ></i>
                                      </a>
                                    )}
                                  </span>
                                  <span className="d-flex align-items-center gap-2">
                                    {x.pine_people > 0 && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setWhoWeKnow({
                                            name: x.name,
                                            mapCompanyId: x.id,
                                          })
                                        }
                                        title={`PINE has ${x.pine_people} ${
                                          x.pine_people === 1 ? "person" : "people"
                                        } here — see who fits this role`}
                                        className="btn rounded-pill d-inline-flex align-items-center gap-1 px-2 py-0 fw-semibold"
                                        style={{
                                          fontSize: 10,
                                          lineHeight: "16px",
                                          background: "#fff",
                                          color: "#15803D",
                                          border: "1px solid rgba(21, 128, 61, 0.4)",
                                        }}
                                      >
                                        <i
                                          className="fa-solid fa-users"
                                          style={{ fontSize: 9 }}
                                        ></i>
                                        PINE {x.pine_people}
                                      </button>
                                    )}
                                    {x.candidates > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => openCandidates(x)}
                                        title={`${x.candidates} candidate${
                                          x.candidates === 1 ? "" : "s"
                                        } mapped — click to view`}
                                        className="btn btn-light border rounded-pill d-inline-flex align-items-center gap-1 px-2 py-0 fw-semibold"
                                        style={{
                                          fontSize: 10,
                                          lineHeight: "16px",
                                        }}
                                      >
                                        <i
                                          className="fa-solid fa-user"
                                          style={{ fontSize: 9 }}
                                        ></i>
                                        {x.candidates}
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => removeCompany(x)}
                                      title={`Remove ${x.name} from the map`}
                                      className="btn btn-link p-0"
                                      style={{
                                        color: "inherit",
                                        opacity: 0.45,
                                        fontSize: 10,
                                      }}
                                    >
                                      <i className="fa-solid fa-xmark"></i>
                                    </button>
                                  </span>
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    <tr>
                      {columns.map((c) => (
                        <td key={c.id} className="border px-2 py-1 bg-white">
                          <button
                            type="button"
                            onClick={() => openAdd(c)}
                            title={`Add a company to ${c.industry}`}
                            className="btn btn-sm w-100 text-muted d-flex align-items-center justify-content-center gap-1"
                            style={{ border: "1px dashed #ccc", fontSize: 11 }}
                          >
                            <i className="fa-solid fa-plus"></i> Add
                          </button>
                        </td>
                      ))}
                    </tr>
                    <tr>
                      {columns.map((c) => (
                        <td key={c.id} className="border px-2 py-1 bg-light">
                          <select
                            className="form-select form-select-sm"
                            style={{ fontSize: 12 }}
                            value={c.owner?.id ?? ""}
                            onChange={(e) => setOwner(c, e.target.value)}
                            title="Who owns this industry"
                          >
                            <option value="">Unassigned</option>
                            {/* An owner who has since left still shows. */}
                            {c.owner &&
                              !users.some((u) => u.id === c.owner.id) && (
                                <option value={c.owner.id}>
                                  {c.owner.name}
                                </option>
                              )}
                            {users.map((u) => (
                              <option key={u.id} value={u.id}>
                                {userName(u)}
                              </option>
                            ))}
                          </select>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-muted small mt-3 mb-0">
                Statuses come from the workstation — a company turns green on
                its own once candidates are mapped against it. Add companies
                with <strong>Add company</strong> above or the{" "}
                <strong>+ Add</strong> row under any column.
              </p>
            </>
          )}
        </div>
      </div>

      {adding && map && (
        <SearchMapAddCompany
          roleId={map.role.id}
          columns={columns}
          presetColumn={adding.column}
          token={token}
          onClose={() => setAdding(null)}
          onAdded={loadMap}
        />
      )}

      {whoWeKnow && map && (
        <WhoWeKnow
          roleId={map.role.id}
          company={whoWeKnow}
          token={token}
          onClose={() => setWhoWeKnow(null)}
          onAdded={loadMap}
        />
      )}

      {viewing && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    {viewing.name} —{" "}
                    {people ? people.length : viewing.candidates} candidate
                    {(people ? people.length : viewing.candidates) === 1
                      ? ""
                      : "s"}{" "}
                    on this role
                  </h4>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setViewing(null)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  {peopleError && (
                    <div className="alert alert-danger mb-0">{peopleError}</div>
                  )}
                  {!peopleError && !people && (
                    <p className="text-muted mb-0">Loading the candidates…</p>
                  )}
                  {people && (
                    <div
                      className="border rounded overflow-auto"
                      style={{ maxHeight: "60vh" }}
                    >
                      <table
                        className="table table-sm table-hover mb-0"
                        style={{ fontSize: 12 }}
                      >
                        <thead className="table-light sticky-top">
                          <tr>
                            {CANDIDATE_COLUMNS.map((h) => (
                              <th key={h} className="text-nowrap">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {people.map((p) => (
                            <tr key={p.id}>
                              <td className="text-muted text-nowrap">{p.id}</td>
                              <td className="fw-medium text-nowrap">
                                {/* Same link as a name on the workstation: the
                                    candidate's page in its own tab. */}
                                <a
                                  href={`/candidates/view/${p.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    openInAppTab(`/candidates/view/${p.id}`);
                                  }}
                                >
                                  {p.name || `Candidate ${p.id}`}
                                </a>
                              </td>
                              <td className="text-nowrap">{p.email || "—"}</td>
                              <td className="text-nowrap">{p.phone || "—"}</td>
                              <td>{p.designation || "—"}</td>
                              <td className="text-nowrap">
                                {p.company || "—"}
                              </td>
                              <td className="text-nowrap">
                                {p.status ? (
                                  <span
                                    className="badge rounded-pill"
                                    style={{
                                      background: "rgba(242,107,42,0.12)",
                                      color: HEADER_ORANGE,
                                    }}
                                    title={p.status_group || ""}
                                  >
                                    {p.status_group
                                      ? `${p.status_group} › ${p.status}`
                                      : p.status}
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setViewing(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
}
